import React, { useCallback, type FC } from 'react'
import { useIntl } from 'react-intl'
import { type Getter, type Setter, useAtom } from 'jotai'

import {
  Panel,
  Flex,
  FormElement,
  InputText,
  FlexItem,
  INPUT_TEXT_VARIANT_DANGER,
  Toggle,
  Text,
} from 'mailup-web-components'

import { filterFormAtom } from '../state/state'
import { dirtyFieldsAtom, isFormNameValidAtom } from '../state/validation'
import { useAtomCallback } from 'jotai/utils'
import { modifyFilterCallback } from '../callbacks/form'
import { type FilterRequest } from '../../../types/beFiltersApi'
import FilterFormCategories from './FilterFormCategories'
import { STORE_KEY_ID_LIST, storeGet } from 'mailup-react-helpers'
import RecipientsCounter from './RecipientsCounter'
import { type UiFilterDetail } from '../filterConditionTypes'

const InfoPanel: FC = () => {
  const intl = useIntl()

  const [{ Name, Notes, IdList, IsUsedInOtherLists }] = useAtom(filterFormAtom)
  const [dirtyFields] = useAtom(dirtyFieldsAtom)
  const [isFormNameValid] = useAtom(isFormNameValidAtom)

  const isMultiList = IdList === 0

  const changeField = useAtomCallback(
    useCallback((get: Getter, set: Setter, part: Partial<UiFilterDetail>) => {
      modifyFilterCallback(get, set, part)
    }, []),
  )
  const handleChangeField =
    (f: keyof FilterRequest) =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      changeField({
        [f]: e.target.value,
      })
    }
  const handleChangeListToggle = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const idList: number = e.target.checked ? 0 : storeGet(STORE_KEY_ID_LIST)
    changeField({ IdList: idList })
  }

  const isDirty = (f: keyof FilterRequest): boolean =>
    dirtyFields.some((d) => d === f)
  const requiredFeedback = intl.formatMessage({
    id: 'Load.MailupCommon.Required',
  })

  return (
    <Panel>
      <Flex
        flexDirection='row'
        gap='24px'
        flexGrow={1}
        alignItems='center'
        justifyContent='space-between'
      >
        <Flex flexDirection='column' gap='24px' padding='16px' flexGrow={1}>
          <Flex
            flexDirection='row'
            gap='24px'
            flexGrow={1}
            alignItems='center'
            justifyContent='space-between'
          >
            <FlexItem flex='1 0 auto'>
              <FormElement
                labelFor='filterForm-Name'
                data-mu-test='filterForm-Name_element'
                required
                label={intl.formatMessage({ id: 'Load.MailupCommon.Name' })}
                feedback={
                  !isFormNameValid && isDirty('Name')
                    ? requiredFeedback
                    : undefined
                }
              >
                <InputText
                  id='filterForm-Name'
                  data-mu-test='filterForm-Name_input'
                  name='Name'
                  value={Name}
                  onChange={handleChangeField('Name')}
                  variant={
                    !isFormNameValid && isDirty('Name')
                      ? INPUT_TEXT_VARIANT_DANGER
                      : undefined
                  }
                />
              </FormElement>
            </FlexItem>
            <FilterFormCategories />
          </Flex>
          <Flex flexDirection='row' gap='24px' flexGrow={1}>
            <FlexItem flex='1 0 auto'>
              <FormElement
                labelFor='filterForm-Notes'
                data-mu-test='filterForm-Notes_Element'
                label={intl.formatMessage({ id: 'Load.MailupCommon.Note' })}
              >
                <InputText
                  id='filterForm-Notes'
                  data-mu-test='filterForm-Notes_input'
                  name='Notes'
                  value={Notes}
                  onChange={handleChangeField('Notes')}
                  maxLength='255'
                  placeholder={intl.formatMessage({
                    id: 'Load.CombinedFilterForm.NotePlaceholder',
                  })}
                />
              </FormElement>
            </FlexItem>
          </Flex>
          <Flex flexDirection='row' gap='24px'>
            <FlexItem>
              <Toggle
                checked={isMultiList}
                onChange={handleChangeListToggle}
                disabled={IsUsedInOtherLists}
              />
            </FlexItem>
            <FlexItem>
              <Text>
                {intl.formatMessage({
                  id: 'Load.CombinedFilterForm.MultiListDesc',
                })}
              </Text>
            </FlexItem>
          </Flex>
        </Flex>
        <RecipientsCounter />
      </Flex>
    </Panel>
  )
}

export default InfoPanel
